<template>
  <div class="grid justify-content-center mt-3">
    <ProgressSpinner
      v-if="loading"
      style="width: 50px; height: 50px"
      strokeWidth="6"
      fill="var(--surface-ground)"
      animationDuration=".5s"
      class="mb-5"
    />
    <div class="w-10 flex gap-5" v-if="!loading && afectacionesPatrimoniales.length">
      <Listbox
        v-model="afectacionSelected"
        :options="afectacionesPatrimoniales"
        dataKey="AfectacionPatrimonialID"
        optionLabel="name"
        ref="listBox"
        class="shadow-4 border-none"
      >
        <template #option="{ option }">
          <div class="flex justify-content-start align-content-center gap-2">
            <span class="mt-2">
              {{ `$ ${option.Monto}.- ${getMonedaByID(option.TipoMonedaID)?.NombreMoneda}` }}
            </span>
            <Tag
              :value="getFormattedTransactionType(option.TipoAfectacionPatrimonial)"
              class="border-round-xl"
              severity="danger"
            />
          </div>
          <div>
            <span
              >{{ option.IdentificadorFinancieroOrigen }} -
              {{ option.IdentificadorFinancieroDestino }}
            </span>
          </div>
        </template>
      </Listbox>
      <div class="col-12 md:col-8 lg:col-9" v-if="afectacionSelected">
        <div>
          <InputText
            :value="getFormattedTransactionType(afectacionSelected?.TipoAfectacionPatrimonial)"
            class="lg:col-5 md:col-7 sm:col-12 mb-3"
            disabled
          />
          <FormDinamico
            :fields="fields"
            :externalData="externalData"
            ref="dinamico"
            storeGetterPath="sumario/afectacionPatrimonial"
            storeGetterName="getAfectacionByArrId"
            storeSetterPath="sumario/afectacionPatrimonial"
            storeSetterName="setAfectacion"
            storeGetterAllName="afectacionesSelect"
            isReadonly
          />
          <div>
            <h2>Datos complementarios</h2>
            <div class="flex flex-column gap-2">
              <label for="idCoelsa">Código Coelsa</label>
              <InputText
                id="idCoelsa"
                :value="idCoelsa"
                class="lg:col-5 md:col-7 sm:col-12 mb-3"
                disabled
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="col-12 md:col-8 lg:col-9 flex justify-content-center">
        <label class="text-xl">Seleccione una afectación patrimonial.</label>
      </div>
    </div>
    <div
      v-if="!loading && !afectacionesPatrimoniales.length"
      class="col-12 px-5 shadow-4 text-center"
    >
      <label class="text-xl">No se ha registrado información patrimonial.</label>
    </div>
  </div>
</template>
<script>
import { useStoreCompat } from "@/composables/useStoreCompat";
import { useMonedasStore } from "@/store/Pinia/useMonedasStore";
import { useAfectacionStore } from "@/store/Pinia/useAfectacionStore";
import Listbox from "primevue/listbox";
import { defineComponent, ref, computed, onMounted, watch } from "vue";
import InputText from "primevue/inputtext/InputText";
import { getFields } from "@/data/fields";
import FormDinamico from "@/components/forms/FormDinamico.vue";
import {
  colorTagPatrimonialAffectation,
  returnCbuOrEstate,
  getFormattedTransactionType,
} from "@/utils/showAfectation";
import { storeToRefs } from "pinia";

export default defineComponent({
  name: "AfectacionTab",
  components: {
    Listbox,
    FormDinamico,
  },
  setup() {
    const store = useStoreCompat();

    const monedasStore = useMonedasStore();
    const afectacionStore = useAfectacionStore();
    const { afectacionesPatrimoniales, loading } = storeToRefs(afectacionStore);

    const afectacionSelected = ref({});
    const fields = ref([]);
    const idCoelsa = ref(null);

    const sumario = computed(() => store.state.sumario);
    const tipoMoneda = computed(() => store.state.siis.tipoMoneda);
    const afectacionesSelect = computed(
      () => store.getters["sumario/afectacionPatrimonial/afectacionesSelect"]
    );
    const getAfectacionByArrId = computed(
      () => store.getters["sumario/afectacionPatrimonial/getAfectacionByArrId"]
    );
    const denuncianteTodos = computed(() => store.state.sumario.denunciante.list);
    const acusadosTodos = computed(() => store.state.sumario.acusados.list);
    const externalData = computed(() => normalizarKeys(afectacionSelected.value));

    const setAfectacion = () => {
      if (afectacionesPatrimoniales.value.length > 0) {
        afectacionSelected.value = afectacionesPatrimoniales.value[0];
      }
      idCoelsa.value = afectacionesPatrimoniales.value[0].codigoCoelsa;
    };

    const getMonedaByID = (id) => {
      return monedasStore.monedas?.find((moneda) => moneda.TipoMonedaID === id);
    };
    const getMonedasOptions = () => {
      const monedas = monedasStore.monedas
        .filter((moneda) => {
          if (afectacionSelected.value.TipoAfectacionPatrimonial === "cripto") {
            return moneda.TipoDeMoneda === "Criptomoneda";
          } else {
            return moneda.TipoDeMoneda !== "Criptomoneda";
          }
        })
        .map((moneda) => ({
          value: moneda.TipoMonedaID,
          nombre: moneda.NombreMoneda,
        }));
      return monedas;
    };

    const updateFields = () => {
      const tipo = afectacionSelected.value?.TipoAfectacionPatrimonial;
      if (!tipo) {
        fields.value = [];
        return;
      }

      fields.value = getFields(
        "afectacionPatrimonial",
        tipo,
        getAfectadosOptions(),
        [...getAcusadosOptions(), { value: "1", nombre: "Persona Desconocida" }],
        [],
        getMonedasOptions(),
        true
      );
    };

    const getAcusadosOptions = () => {
      if (!acusadosTodos.value || acusadosTodos.value.length === 0) return;
      if (!acusadosTodos.value[0].PersonaFisicaID) {
        return acusadosTodos.value.map((acusado) => ({
          value: acusado.arrId,
          nombre:
            acusado.apellido && acusado.nombre
              ? `${acusado.apellido}, ${acusado.nombre}`
              : "Persona desconocida",
        }));
      } else {
        return acusadosTodos.value.map((acusado) => ({
          value: acusado.PersonaFisicaID,
          nombre:
            acusado.Apellido && acusado.Nombre
              ? `${acusado.Apellido},${acusado.Nombre}`
              : "Persona desconocida",
        }));
      }
    };

    const getAfectadosOptions = () => {
      if (!denuncianteTodos.value || denuncianteTodos.value.length === 0) return;
      if (!denuncianteTodos.value[0].PersonaFisicaID) {
        return denuncianteTodos.value.map((denunciante) => ({
          value: denunciante.arrId,
          nombre:
            denunciante.Apellido && denunciante.Nombre
              ? `${denunciante.Apellido}, ${denunciante.Nombre}`
              : "Persona desconocida",
        }));
      } else {
        return denuncianteTodos.value.map((denunciante) => ({
          value: denunciante.PersonaFisicaID,
          nombre:
            denunciante.Apellido && denunciante.Nombre
              ? `${denunciante.Apellido},${denunciante.Nombre}`
              : "Persona desconocida",
        }));
      }
    };
    const getNameAfectado = (id) => {
      const item = getAfectadosOptions().find((afectado) => afectado.value === id);

      return item?.nombre || "Sin afectado";
    };

    const getNameVinculado = (id) => {
      if (!id) return "Persona desconocida";
      const vinculado = getAcusadosOptions().find((acusado) => acusado.value === id);
      return vinculado?.nombre || "Persona desconocida";
    };
    const normalizarKeys = (obj) => {
      if (!obj) return {};
      return {
        monto: obj.Monto,
        tipoMoneda: {
          nombre: getMonedaByID(obj.TipoMonedaID)?.NombreMoneda,
          value: obj.TipoMonedaID,
        },
        identificadorOrigen: obj.IdentificadorFinancieroOrigen,
        identificadorDestino: obj.IdentificadorFinancieroDestino,
        entidadBancaria: obj.EntidadBancaria,
        fecha: obj.FechaTransaccion,
        esPropietario: {
          nombre: +obj.PropietarioCuenta === 1 ? "Sí" : "No",
          value: +obj.PropietarioCuenta,
        },
        vinculadoId: {
          nombre: getNameVinculado(obj.VinculadoPersonaFisicaID),
          value: obj.VinculadoPersonaFisicaID ?? "Persona de Filiacion desconocida",
        },
        afectadoId: {
          nombre: getNameAfectado(obj.AfectadoPersonaFisicaID),
          value: obj.AfectadoPersonaFisicaID,
        },
        descripcion: obj.Observaciones,
        observaciones: obj.Descripcion,
        NroTarjeta: obj.NumeroTarjeta,
        dni: obj.Dni,
        nombreCompleto: obj.NombreCompleto,
        bienEntregado: obj.BienEntregadoID,
      };
    };

    watch(
      () => afectacionSelected.value?.TipoAfectacionPatrimonial,
      () => {
        updateFields();
      },
      { immediate: true }
    );

    onMounted(async () => {
      try {
        await Promise.all([
          afectacionStore.handleFetchAfectaciones(sumario.value?.id),
          monedasStore.setMonedas(),
          store.dispatch("sumario/fetchAfectados", sumario.value?.id),
          store.dispatch("sumario/fetchVinculados", sumario.value?.id),
        ]);

        setAfectacion();
      } finally {
        loading.value = false;
      }
    });

    return {
      //refs
      loading,
      afectacionesPatrimoniales,
      afectacionSelected,
      fields,
      idCoelsa,

      //computed
      getMonedaByID,
      tipoMoneda,
      afectacionesSelect,
      getAfectacionByArrId,
      denuncianteTodos,
      acusadosTodos,
      externalData,

      //methods
      colorTagPatrimonialAffectation,
      returnCbuOrEstate,
      getFormattedTransactionType,
      normalizarKeys,
    };
  },
});
</script>
<style scoped>
.input-order {
  min-width: 250px;
  margin-top: -17px;
}
</style>
